import React, { useEffect, useMemo, useState } from "react";
import { FiDownload, FiX } from "react-icons/fi";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const DISMISSED_STORAGE_KEY = "onetopPwaInstallPromptDismissedAt";
const DISMISS_COOLDOWN_DAYS = 7;

const isStandaloneMode = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  (window.navigator as any).standalone === true;

const shouldRespectDismissCooldown = () => {
  const dismissedAt = Number(
    window.localStorage.getItem(DISMISSED_STORAGE_KEY) || 0,
  );
  if (!dismissedAt) return false;

  const cooldownMs = DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - dismissedAt < cooldownMs;
};

export default function PwaInstallPrompt() {
  const [installEvent, setInstallEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  const canInstall = useMemo(
    () => Boolean(installEvent) && !isStandaloneMode(),
    [installEvent],
  );

  useEffect(() => {
    if (isStandaloneMode()) return undefined;

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      const promptEvent = event as BeforeInstallPromptEvent;
      setInstallEvent(promptEvent);

      if (!shouldRespectDismissCooldown()) {
        setIsOpen(true);
      }
    };

    const handleAppInstalled = () => {
      setInstallEvent(null);
      setIsOpen(false);
      window.localStorage.removeItem(DISMISSED_STORAGE_KEY);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const closePrompt = () => {
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, String(Date.now()));
    setIsOpen(false);
  };

  const installApp = async () => {
    if (!installEvent) return;

    setIsInstalling(true);
    try {
      await installEvent.prompt();
      await installEvent.userChoice;
      setInstallEvent(null);
      setIsOpen(false);
    } finally {
      setIsInstalling(false);
    }
  };

  if (!canInstall) return null;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-4 z-[1080] flex justify-center px-3 pb-[env(safe-area-inset-bottom)]"
      role="status"
      aria-live="polite"
    >
      <div className="flex w-full max-w-[520px] items-center gap-3 rounded-lg border border-[#dbe3ec] bg-white p-3 shadow-[0_18px_44px_rgba(15,23,42,0.18)] dark:border-slate-800 dark:bg-slate-950 dark:shadow-[0_18px_44px_rgba(0,0,0,0.42)]">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FiDownload className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="mb-0 truncate text-[14px] font-bold text-slate-900 dark:text-slate-100">
            ຕິດຕັ້ງ OneTop
          </p>
          <p className="mb-0 truncate text-[12px] font-semibold text-slate-500 dark:text-slate-400">
            ເປີດໃຊ້ງານໄດ້ໄວຂຶ້ນເໝືອນແອັບ
          </p>
        </div>
        <button
          type="button"
          onClick={installApp}
          disabled={isInstalling}
          className="inline-flex h-10 flex-none items-center justify-center gap-2 rounded-lg bg-primary px-3 text-[12px] font-bold text-white transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <FiDownload className="h-4 w-4" />
          {isInstalling ? "..." : "ຕິດຕັ້ງ"}
        </button>
        <button
          type="button"
          onClick={closePrompt}
          className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-slate-200"
          aria-label="Dismiss install prompt"
          title="Dismiss"
        >
          <FiX className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
